"use client";

import Image from "next/image";
import Link from "next/link";

import { Card, CardContent, CardFooter } from "@unreserved/ui/components/card";

import { CheckMarkIcon } from "@/app/_components/icons/landing-page/check-mark";
import { XCircleIcon } from "@/app/_components/icons/landing-page/x-circle";
import { Button } from "@/app/_components/shared/button";
import type { PricingCard as PricingCardData } from "@/app/_libs/data/landing-page/pricing-section";

interface PricingCardProps {
  card: PricingCardData;
}

export function PricingCard({ card }: PricingCardProps) {
  return (
    <Card className="bg-background-0 border-border-100 relative flex min-h-px min-w-px flex-col justify-between rounded-xl border [box-shadow:0_12px_16px_-4px_rgba(16,24,40,0.08),0_4px_6px_-2px_rgba(16,24,40,0.03)]">
      <div className="absolute top-6 -right-5 z-10 lg:-right-3">
        <div className="relative">
          <Image
            src={card.badgeImage}
            alt={`${card.title} price`}
            width={124}
            height={57}
            className="h-[35px] w-[96px] md:h-[40px] md:w-[110px] lg:h-auto lg:w-[124px]"
          />
          <span
            className={`absolute inset-y-0 right-1 bottom-2 left-0 flex items-center justify-center text-xs font-medium tracking-[0.2px] md:text-base lg:text-lg ${card.priceTextClass}`}>
            {card.price}
          </span>
        </div>
      </div>

      <CardContent className="relative flex w-full flex-col items-center justify-center gap-6">
        <div className="relative flex w-full items-center gap-2.5">
          <h3 className="text-typography-800 relative text-center text-lg leading-normal font-bold tracking-[0.2px] lg:text-2xl">
            {card.title}
          </h3>
        </div>
        <div className="relative flex w-full flex-col items-start gap-4">
          {card.features.map((feature, index) => (
            <div key={index} className="relative flex w-full items-start gap-3">
              {feature.included ? <CheckMarkIcon /> : <XCircleIcon />}
              <p className="text-typography-700 relative min-h-px min-w-px flex-1 text-xs leading-normal font-medium lg:text-base">
                {feature.text}
                {feature.links && feature.links.length > 0 && (
                  <>
                    {" "}
                    {feature.links.map((link, linkIndex) => (
                      <span key={link.href}>
                        {linkIndex > 0 && ", "}
                        <Link
                          href={link.href as unknown as never}
                          target="_blank"
                          rel="noreferrer"
                          className="underline">
                          {link.label}
                        </Link>
                      </span>
                    ))}
                  </>
                )}
              </p>
            </div>
          ))}
        </div>
      </CardContent>

      <CardFooter className="relative flex w-full items-center gap-3 px-4 pt-0 pb-6 md:gap-4 md:px-6">
        <Button className="bg-background-0 text-typography-900 border-typography-muted md:bg-typography-muted md:text-background-0 hover:bg-background-0 hover:text-typography-900 hover:border-typography-muted md:hover:bg-typography-muted md:hover:text-background-0 h-9 flex-[0.8] cursor-pointer border px-3 text-xs md:flex-1 md:border-transparent md:px-2 md:hover:border-transparent lg:h-11 lg:text-base">
          <Link href={card.secondaryCtaHref as never}>{card.secondaryCtaLabel}</Link>
        </Button>

        <Button className="bg-primary-700 text-background-0 hover:bg-primary-700 hover:text-background-0 h-9 flex-[1.2] cursor-pointer px-3 text-xs md:flex-1 md:px-4 lg:h-11 lg:text-base">
          <Link href={card.primaryCtaHref as never}>{card.primaryCtaLabel}</Link>
        </Button>
      </CardFooter>
    </Card>
  );
}
